import { appDataDir, join } from '@tauri-apps/api/path'
import { save } from '@tauri-apps/plugin-dialog'
import { writeFile, mkdir, BaseDirectory, exists } from '@tauri-apps/plugin-fs'
import { openPath } from '@tauri-apps/plugin-opener'

import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isTauri } from '@/services/platformService'

interface DownloadOptions {
  mimeType?: string
}

const DEFAULT_MIME_TYPE = 'application/octet-stream'
const TAURI_DOWNLOADS_DIR = 'downloads'

const sanitizeDownloadName = (fileName: string): string => {
  const trimmed = fileName.trim() || 'download'
  return trimmed.replace(/[\\/:*?"<>|]/g, '_')
}

const downloadInBrowser = (blob: Blob, fileName: string, mimeType: string): void => {
  const objectUrl = URL.createObjectURL(new Blob([blob], { type: mimeType }))
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()

  requestAnimationFrame(() => {
    requestAnimationFrame(() => URL.revokeObjectURL(objectUrl))
  })
}

const downloadInTauri = async (blob: Blob, fileName: string, mimeType: string): Promise<void> => {
  const safeName = sanitizeDownloadName(fileName)
  const bytes = new Uint8Array(await blob.arrayBuffer())
  const extension = safeName.match(/\.([^.]+)$/)?.[1]?.toLowerCase()
  const filterExtensions = [
    extension,
    mimeType !== DEFAULT_MIME_TYPE ? mimeType : undefined,
  ].filter((value): value is string => Boolean(value))

  try {
    const selectedPath = await save({
      defaultPath: safeName,
      filters: filterExtensions.length
        ? [
            {
              name: extension?.toUpperCase() || 'File',
              extensions: filterExtensions,
            },
          ]
        : undefined,
    })

    if (!selectedPath) {
      return
    }

    await writeFile(selectedPath, bytes)
    return
  } catch (error) {
    logService.warn(
      LOG_CATEGORIES.FILE,
      'Native save dialog unavailable, falling back to app-data download',
      {
        error,
        fileName: safeName,
      },
    )
  }

  const targetDirectoryExists = await exists(TAURI_DOWNLOADS_DIR, {
    baseDir: BaseDirectory.AppData,
  })
  if (!targetDirectoryExists) {
    await mkdir(TAURI_DOWNLOADS_DIR, { baseDir: BaseDirectory.AppData, recursive: true })
  }

  const filePath = `${TAURI_DOWNLOADS_DIR}/${Date.now()}-${safeName}`

  await writeFile(filePath, bytes, { baseDir: BaseDirectory.AppData })
  const absolutePath = await join(await appDataDir(), filePath)
  await openPath(absolutePath)
}

/**
 * Triggers a browser download for the provided blob.
 */
export const downloadFile = async (
  blob: Blob,
  fileName: string,
  options?: DownloadOptions,
): Promise<void> => {
  if (typeof window === 'undefined') {
    return
  }

  const resolvedName = fileName.trim() || 'download'
  const mimeType = options?.mimeType || blob.type || DEFAULT_MIME_TYPE

  if (isTauri()) {
    await downloadInTauri(new Blob([blob], { type: mimeType }), resolvedName, mimeType)
    return
  }

  downloadInBrowser(blob, resolvedName, mimeType)
}
